import React, { useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import BasePersonTimelineRecordLayout from 'domain/shared/timeline/person/PersonTimelineRecord/BasePersonTimelineRecordLayout';
import { useShowContext } from 'framework/show';
import { Collapsible, HtmlField } from 'framework/ui/field';
import { DATE_FORMAT, ReplaceUrlWithLink, useSubmit } from 'framework/utils';
import {
  personTimelineActionNameSx,
  personTimelineDateSx,
  personTimelineHeaderSx,
} from 'domain/shared/timeline/person/PersonTimelineRecord/personTimelineRecordSx';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  useProjectSecurityContext,
  useNewProjectUrl,
} from 'domain/shared/newProject';
import useProjectNotesService from './useProjectNotesService';
import { useProjectId } from 'domain/shared/project';
import { Box, Typography } from '@mui/material';
import MyLocation from '@mui/icons-material/MyLocation';
import { CLOSEOUT_STATUS_OPTIONS, LINK_TYPE_OPTIONS, PROJECT_TIMELINE_MESSAGE_CONTEXT } from './constants';
import { Grid } from '@mui/system';
import DeleteConfirmationModal from './shared/DeleteConfirmationModal';
import { useListContext } from 'framework/list';
import { Badge } from 'framework/ui/mui-builder';
import { ITimelineItem, ProjectCloseoutNote } from './types';
import { EmployeeLinkFieldWithNameLoading } from 'domain/shared/employee';
import { ShowDate } from 'framework/ui/ui-builder';

const ProjectCloseoutTimelineRecordHeader = () => {
  const { data: entity } = useShowContext();

  return (
    <Box sx={personTimelineHeaderSx}>
      <Box sx={personTimelineActionNameSx}>
        <EmployeeLinkFieldWithNameLoading value={entity?.updatedAt ? entity?.updatedBy : entity?.createdBy} />
      </Box>
      <Box sx={personTimelineDateSx}>
        <ShowDate dateUTC={entity?.updatedAt ? entity?.updatedAt : entity?.createdAt} format={DATE_FORMAT.FULL} />
      </Box>
    </Box>
  );
};

const PROJECT_CLOSEOUT_TIMELINE_ICONS = {
  Closeout: MyLocation,
  default: MyLocation,
};

const ProjectCloseoutTimelineRecordLayout = (props: Record<string, unknown>) => {
  const { isAccessGranted } = useProjectSecurityContext();
  const { data: entity } = useShowContext<ITimelineItem<ProjectCloseoutNote>>();
  const { deleteUserNote } = useProjectNotesService();
  const { getProjectTimelineUrl } = useNewProjectUrl();
  const projectId = useProjectId();
  const basePathname = getProjectTimelineUrl(projectId);
  const { deleteFromList } = useListContext();

  const payload = entity?.payload || {};
  const status = payload.status;
  const totalBudgetSpentHours = payload.totalHoursSpent;
  const deliveryDate = payload.deliveryDate;
  const links = payload.links || [];

  const [modalOpen, setModalOpen] = useState(false);
  const { onSubmit, isLoading } = useSubmit({
    submitMethod: () => deleteUserNote(entity.id),
    afterSubmit: { execute: () => (deleteFromList ? deleteFromList(entity.timelineId) : null) },
  });

  const canManage = isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_TIMELINE_ENTRY, ACCESS_INFO_ACTIONS.MANAGE);
  const canPartialManage = isAccessGranted(
    ACCESS_INFO_SECTIONS.PROJECT_TIMELINE_ENTRY,
    ACCESS_INFO_ACTIONS.PARTIAL_MANAGE,
  );

  const statusOption = CLOSEOUT_STATUS_OPTIONS.find(opt => opt.value === status);
  const statusLabel = statusOption?.label || status;

  const buttons = [
    (canManage || canPartialManage) && (
      <MenuItem key="edit" onClick={props.onEdit as React.MouseEventHandler<HTMLLIElement>}>
        Edit closeout
      </MenuItem>
    ),
    canManage && (
      <MenuItem key="delete" onClick={() => setModalOpen(true)}>
        Delete closeout
      </MenuItem>
    ),
  ];

  const renderCloseoutFields = () => (
    <Box gap={1}>
      {status && (
        <Grid container spacing={1} alignItems="center">
          <Typography variant="body1" component="span" color="textSecondary">
            Status:
          </Typography>
          <Badge sx={{ ml: 1 }} color="error">
            {statusLabel}
          </Badge>
        </Grid>
      )}

      {totalBudgetSpentHours && (
        <Grid container spacing={1} alignItems="center">
          <Typography variant="body1" component="span" color="textSecondary">
            Total Budget Spent, hours:
          </Typography>
          <Typography>{totalBudgetSpentHours}</Typography>
        </Grid>
      )}

      {deliveryDate && (
        <Grid container spacing={1} alignItems="center">
          <Typography variant="body1" component="span" color="textSecondary">
            Delivery Date:
          </Typography>
          <ShowDate dateUTC={deliveryDate} format={DATE_FORMAT.API_ONLY_DATE} />
        </Grid>
      )}

      {links.length > 0 && (
        <>
          <Typography variant="body1" component="span" color="textSecondary">
            Links:{' '}
          </Typography>
          {links.map((link: any, index: number) => {
            const linkTypeOption = LINK_TYPE_OPTIONS.find(option => option.value === link.type);
            const linkLabel = linkTypeOption?.label || link.type;

            return (
              <>
                {!!index && ', '}
                <a href={link.url} target="_blank" rel="noopener noreferrer">
                  {linkLabel}
                </a>
              </>
            );
          })}
        </>
      )}

      {payload.text && (
        <Collapsible>
          <Grid container alignItems="center">
            <Typography variant="body1" component="span" color="textSecondary">
              Note:{' '}
            </Typography>
            <ReplaceUrlWithLink>
              <HtmlField source="payload.text" label="" />
            </ReplaceUrlWithLink>
          </Grid>
        </Collapsible>
      )}
    </Box>
  );

  return (
    <>
      <DeleteConfirmationModal
        isOpen={modalOpen}
        isLoading={isLoading}
        message="Do you really want to delete this closeout?"
        onConfirm={onSubmit}
        onCancel={() => setModalOpen(false)}
      />
      <BasePersonTimelineRecordLayout
        actionName="closeout"
        timelineType="Closeout"
        buttons={buttons}
        timelineIcons={PROJECT_CLOSEOUT_TIMELINE_ICONS}
        timelineHeader={ProjectCloseoutTimelineRecordHeader}
        basePathname={basePathname}
        fields={renderCloseoutFields()}
        permissionToCheck={null}
        isCandidateService={false}
        timelineFooter={() => null}
        isShowButtons={() => true}
        {...props}
        messageContext={PROJECT_TIMELINE_MESSAGE_CONTEXT}
      />
    </>
  );
};

export default ProjectCloseoutTimelineRecordLayout;
